<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
    uid: { type: String, default: '' },
    linkUrl: { type: String, default: '' },
    linkText: { type: String, default: '' },
    linkTarget: { type: String, default: '' },
});

const stripCommas = (str) => (str || '').toString().replace(/,/g, '');
const withCommas = (value) => {
    const num = parseFloat(value);
    if (!isFinite(num)) return '0';
    return num.toLocaleString('en-NZ', { maximumFractionDigits: 2 });
};

const fields = reactive({
    property_price: '700,000',
    mortgage_price: '700,000',
    rent_per_week: '500',
    interest_rate: '5',
    mortgage_term: '30',
    vacancy: '2',
    property_management_gst: '8.3',
    tenant_sourcing_fee: '1',
    maintenance: '500',
    rates: '2,500',
    insurance: '1,800',
    body_corp: '500',
    accounting: '1,600',
});

const mortgageType = ref('interest_only');
const dropdownOpen = ref(false);
const dropdownRef = ref(null);
const resultsOuterRef = ref(null);
const resultsInnerRef = ref(null);

const mortgagesAccordionOpen = ref(false);
const operatingCostsAccordionOpen = ref(false);

const mortgageTypeOptions = [
    { value: 'interest_only', label: 'Interest Only' },
    { value: 'principal_and_interest', label: 'Principal and Interest' },
];

const selectedMortgageLabel = computed(() =>
    mortgageTypeOptions.find(o => o.value === mortgageType.value)?.label ?? ''
);

function toggleDropdown() {
    dropdownOpen.value = !dropdownOpen.value;
}

function selectOption(option) {
    mortgageType.value = option.value;
    dropdownOpen.value = false;
}

function onClickOutside(e) {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
        dropdownOpen.value = false;
    }
}

function numVal(key) {
    const raw = stripCommas(fields[key]);
    return raw !== '' && !isNaN(raw) ? parseFloat(raw) : 0;
}

function handleInput(event, key) {
    const el = event.target;
    const raw = stripCommas(el.value);

    if (raw === '' || isNaN(raw) || /\.$/.test(el.value)) {
        fields[key] = el.value;
        return;
    }

    const posFromEnd = el.value.length - el.selectionStart;
    fields[key] = withCommas(raw);

    nextTick(() => {
        const newPos = el.value.length - posFromEnd;
        el.setSelectionRange(newPos, newPos);
    });
}

function handleBlur(event, key) {
    const raw = stripCommas(event.target.value);
    if (raw !== '' && !isNaN(raw)) {
        fields[key] = withCommas(raw);
    }
}

const WEEKS = 52;

const rentCollected = computed(() => {
    const gross = numVal('rent_per_week') * WEEKS;
    const loss = numVal('vacancy') * numVal('rent_per_week');
    return Math.max(gross - loss, 0);
});

const interestCost = computed(() => {
    const mp = numVal('mortgage_price');
    const rate = numVal('interest_rate');
    const term = numVal('mortgage_term');
    const principalRepayAnnual =
        mortgageType.value === 'principal_and_interest' && term ? mp / term : 0;
    const adjusted =
        mortgageType.value === 'principal_and_interest' ? mp - principalRepayAnnual / 2 : mp;
    return adjusted * rate / 100;
});

const principalRepay = computed(() => {
    if (mortgageType.value !== 'principal_and_interest') return 0;
    const term = numVal('mortgage_term');
    if (!term) return 0;
    const mp = numVal('mortgage_price');
    const rate = numVal('interest_rate');
    const r = (rate / 100) / 12;
    const n = term * 12;
    const m = r === 0 ? mp / n : (mp * r) / (1 - Math.pow(1 + r, -n));
    return (m * 12) - interestCost.value;
});

const operatingCosts = computed(() => {
    const mgmtFee = rentCollected.value * numVal('property_management_gst') / 100;
    const tenantFeeLoss = numVal('tenant_sourcing_fee') * numVal('rent_per_week');
    return mgmtFee + tenantFeeLoss + numVal('maintenance') + numVal('rates')
        + numVal('insurance') + numVal('body_corp') + numVal('accounting');
});

const annualCashflow = computed(() =>
    rentCollected.value - operatingCosts.value - interestCost.value - principalRepay.value
);

const weeklyCashflow = computed(() => annualCashflow.value / WEEKS);

const cashflowNote = 'Estimated weekly income after all forecast expenses are paid'

const displayed = reactive({
    weeklyCashflow: 0,
    rentCollected: 0,
    rates: 0,
    operatingCosts: 0,
    interestCost: 0,
    principalRepay: 0,
});

function animateValue(key, targetValue, duration = 1000) {
    const start = isFinite(displayed[key]) ? displayed[key] : 0;
    const end = isFinite(targetValue) ? Math.round(targetValue) : 0;
    const diff = end - start;
    if (diff === 0) { displayed[key] = end; return; }
    const t0 = performance.now();

    const step = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        displayed[key] = Math.round(start + diff * p);
        if (p < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
}

watch(weeklyCashflow, (v) => animateValue('weeklyCashflow', v));
watch(rentCollected, (v) => animateValue('rentCollected', v));
watch(() => numVal('rates'), (v) => animateValue('rates', v));
watch(operatingCosts, (v) => animateValue('operatingCosts', v));
watch(interestCost, (v) => animateValue('interestCost', v));
watch(principalRepay, (v) => animateValue('principalRepay', v));

function updateMinHeight() {
    if (resultsOuterRef.value && resultsInnerRef.value) {
        resultsOuterRef.value.style.minHeight = resultsInnerRef.value.offsetHeight + 'px';
    }
}

watch([weeklyCashflow, rentCollected, operatingCosts, interestCost, principalRepay], () => {
    nextTick(updateMinHeight);
});

onMounted(() => {
    document.addEventListener('click', onClickOutside);
    animateValue('weeklyCashflow', weeklyCashflow.value);
    animateValue('rentCollected', rentCollected.value);
    animateValue('rates', numVal('rates'));
    animateValue('operatingCosts', operatingCosts.value);
    animateValue('interestCost', interestCost.value);
    animateValue('principalRepay', principalRepay.value);
    nextTick(updateMinHeight);
});

onUnmounted(() => {
    document.removeEventListener('click', onClickOutside);
});
</script>

<template>
    <div class="container">
        <div class="row row-calculator">
            <div class="column column-calculator" data-fade-in>
                <div class="block-input-section">
                    <div class="group">

                        <h5>1. Estimate cashflow for your investment property</h5>
                    <p>Complete this section first. You can use the dropdowns below to make adjustments to assumptions.</p>

                    <div class="input-container">
                        <label for="propertyPrice">Property Price<span class="c-highlight"></span></label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="propertyPrice"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="700,000"
                                :value="fields.property_price"
                                class="calc-input"
                                required
                                @input="handleInput($event, 'property_price')"
                                @blur="handleBlur($event, 'property_price')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="mortgagePrice">Mortgage Price<span class="c-highlight"></span></label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="mortgagePrice"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="700,000"
                                :value="fields.mortgage_price"
                                class="calc-input"
                                required
                                @input="handleInput($event, 'mortgage_price')"
                                @blur="handleBlur($event, 'mortgage_price')"
                            >
                        </div>
                        <p class="note c-darkgrey">Make this the same as the Property Price, unless you have a cash deposit.</p>
                    </div>

                    <div class="input-container">
                        <label for="rentPerWeek">Rent Per Week</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="rentPerWeek"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="500"
                                :value="fields.rent_per_week"
                                class="calc-input"
                                @input="handleInput($event, 'rent_per_week')"
                                @blur="handleBlur($event, 'rent_per_week')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="interestRate">Interest Rate<span class="c-highlight"></span></label>
                        <div class="input_container input_container--suffix-end-lg row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">%</span>
                            </div>
                            <input
                                type="text"
                                id="interestRate"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="5"
                                :value="fields.interest_rate"
                                class="calc-input"
                                required
                                @input="handleInput($event, 'interest_rate')"
                                @blur="handleBlur($event, 'interest_rate')"
                            >
                        </div>
                    </div>
                    </div>

                    <div class="accordion" :class="{ open: mortgagesAccordionOpen }">
                        <div class="accordion-header" @click="mortgagesAccordionOpen = !mortgagesAccordionOpen">
                            <h5>2. Assumptions <span class="tag">MORTGAGES</span></h5>
                            <svg class="accordion-icon" width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 12L0.0717964 0L13.9282 0L7 12Z" fill="#FF7144"/>
                            </svg>
                        </div>
                        <div class="accordion-content">
                            <div class="accordion-inner">
                            <div class="accordion-panel">
                            <div class="input-container">
                                <label>Mortgage Type</label>
                                <div
                                    class="dropdown_container"
                                    :class="{ open: dropdownOpen }"
                                    ref="dropdownRef"
                                    @click.stop="toggleDropdown"
                                >
                                    <div class="selected_option">{{ selectedMortgageLabel }}</div>
                                    <div class="dropdown_icon">
                                        <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1.31006 1.5L5.31006 5.5L9.31006 1.5" stroke="#0C1D3E" stroke-linecap="round" stroke-linejoin="round"></path>
                                        </svg>
                                    </div>
                                    <ul class="options_list">
                                        <li
                                            v-for="opt in mortgageTypeOptions"
                                            :key="opt.value"
                                            :class="{ active: mortgageType === opt.value }"
                                            @click.stop="selectOption(opt)"
                                        >{{ opt.label }}</li>
                                    </ul>
                                </div>
                                <p class="note c-darkgrey">Only used if your mortgage type is principal and interest.</p>
                            </div>

                            <div class="input-container">
                                <label for="mortgageTerm">Mortgage Term</label>
                                <div class="input_container input_container--suffix-end-lg row-1">
                                    <div class="col a-cc input_value_con theme_dark">
                                        <p class="word bold">years</p>
                                    </div>
                                    <input
                                        type="text"
                                        id="mortgageTerm"
                                        inputmode="decimal"
                                        pattern="[0-9,]*[.]?[0-9]*"
                                        placeholder="30"
                                        :value="fields.mortgage_term"
                                        class="calc-input"
                                        @input="handleInput($event, 'mortgage_term')"
                                        @blur="handleBlur($event, 'mortgage_term')"
                                    >
                                </div>
                            </div>
                            </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion" :class="{ open: operatingCostsAccordionOpen }">
                        <div class="accordion-header" @click="operatingCostsAccordionOpen = !operatingCostsAccordionOpen">
                            <h5>3. Assumptions <span class="tag">OPERATING COSTS</span></h5>
                            <svg class="accordion-icon" width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 12L0.0717964 0L13.9282 0L7 12Z" fill="#FF7144"/>
                            </svg>
                        </div>
                        <div class="accordion-content">
                            <div class="accordion-inner">
                            <div class="accordion-panel">

                    <div class="input-container">
                        <label for="vacancy">Vacancy</label>
                        <div class="input_container input_container--suffix-end-lg row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <p class="word bold">weeks</p>
                            </div>
                            <input
                                type="text"
                                id="vacancy"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="2"
                                :value="fields.vacancy"
                                class="calc-input"
                                @input="handleInput($event, 'vacancy')"
                                @blur="handleBlur($event, 'vacancy')"
                            >
                        </div>
                        <p class="note c-darkgrey">The number of weeks that you don't have a tenant per year.</p>
                    </div>

                    <div class="input-container">
                        <label for="propertyManagement">Property Management</label>
                        <div class="input_container input_container--suffix-end-lg row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">%</span>
                            </div>
                            <input
                                type="text"
                                id="propertyManagement"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="8.3"
                                :value="fields.property_management_gst"
                                class="calc-input"
                                @input="handleInput($event, 'property_management_gst')"
                                @blur="handleBlur($event, 'property_management_gst')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="tenantSourcingFee">Tenant Sourcing Fee</label>
                        <div class="input_container input_container--suffix-end-lg row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <p class="word bold">weeks</p>
                            </div>
                            <input
                                type="text"
                                id="tenantSourcingFee"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="1"
                                :value="fields.tenant_sourcing_fee"
                                class="calc-input"
                                @input="handleInput($event, 'tenant_sourcing_fee')"
                                @blur="handleBlur($event, 'tenant_sourcing_fee')"
                            >
                        </div>
                        <p class="note c-darkgrey">The cost of the property manager to find a new one.</p>
                    </div>

                    <div class="input-container">
                        <label for="maintenance">Maintenance</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="maintenance"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="500"
                                :value="fields.maintenance"
                                class="calc-input"
                                @input="handleInput($event, 'maintenance')"
                                @blur="handleBlur($event, 'maintenance')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="rates">Rates</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="rates"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="2,500"
                                :value="fields.rates"
                                class="calc-input"
                                @input="handleInput($event, 'rates')"
                                @blur="handleBlur($event, 'rates')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="insurance">Insurance</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="insurance"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="1,800"
                                :value="fields.insurance"
                                class="calc-input"
                                @input="handleInput($event, 'insurance')"
                                @blur="handleBlur($event, 'insurance')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="bodyCorp">Body Corp or Resident's Association</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="bodyCorp"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="500"
                                :value="fields.body_corp"
                                class="calc-input"
                                @input="handleInput($event, 'body_corp')"
                                @blur="handleBlur($event, 'body_corp')"
                            >
                        </div>
                    </div>

                    <div class="input-container">
                        <label for="accounting">Accounting</label>
                        <div class="input_container row-1">
                            <div class="col a-cc input_value_con theme_dark">
                                <span class="symbol">$</span>
                            </div>
                            <input
                                type="text"
                                id="accounting"
                                inputmode="decimal"
                                pattern="[0-9,]*[.]?[0-9]*"
                                placeholder="1,600"
                                :value="fields.accounting"
                                class="calc-input"
                                @input="handleInput($event, 'accounting')"
                                @blur="handleBlur($event, 'accounting')"
                            >
                        </div>
                    </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="column column-results">
                <div class="results-outter-con" ref="resultsOuterRef">
                    <div class="results-con" ref="resultsInnerRef" data-fade-in-up>
                        <div class="results-header">
                            <h5>YOUR RESULTS:</h5>
                            <p class="results-title">Cashflow per week: $<span class="cashflow_per_week">{{ withCommas(displayed.weeklyCashflow) }}</span></p>
                            <p class="cashflow-note">{{ cashflowNote }}</p>
                            <a v-if="linkUrl" :href="linkUrl" class="link link-fill-arrow white-btn" :target="linkTarget || null" :rel="linkTarget ? 'noopener noreferrer' : null">
                                <span>{{ linkText || 'Talk To An Expert' }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12.286" height="17.414" viewBox="0 0 12.286 17.414">
                                <path data-name="Path 667" d="M1.78,0,7.216,5.436,1.78,10.872M-8.784,5.436H6.591" transform="translate(11.579 8.784) rotate(90)" fill="none" stroke="#000" stroke-width="2"/>
                                </svg>
                            </a>
                        </div>

                        <div class="results-breakdown">
                            <div class="row results-value-con a">
                                <p class="results-heading">CASHFLOW BREAKDOWN:</p>
                            </div>
                            <div class="row results-value-con a" :class="{ hide: rentCollected === 0 }">
                                <p>Annual Income</p>
                                <p class="annual-rent-value">${{ withCommas(displayed.rentCollected) }}</p>
                            </div>
                            <div class="row results-value-con a" :class="{ hide: numVal('rates') === 0 }">
                                <p>Annual Rates</p>
                                <p class="annual-rates-value">${{ withCommas(displayed.rates) }}</p>
                            </div>
                            <div class="row results-value-con a" :class="{ hide: operatingCosts === 0 }">
                                <p>Operating Costs</p>
                                <p class="operating-costs-value">${{ withCommas(displayed.operatingCosts) }}</p>
                            </div>
                            <div class="row results-value-con a" :class="{ hide: interestCost === 0 }">
                                <p>Interest Costs</p>
                                <p class="interest-costs-value">${{ withCommas(displayed.interestCost) }}</p>
                            </div>
                            <div class="row results-value-con a" :class="{ hide: principalRepay === 0 }">
                                <p>Principal Repayments</p>
                                <p class="principal-repayments-value">${{ withCommas(displayed.principalRepay) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
